/**
 * BulkAcceptResultDialog — okno wyniku akcji „Akceptuj zaznaczone", pokazywane TYLKO gdy część
 * spraw została pominięta (pełny sukces kończy się samym dymkiem).
 *
 * Nagłówek niesie liczniki („Zaakceptowano X, pominięto Y"), treść — listę pominiętych spraw:
 * numer sprawy · czego dotyczy (etykieta z wiersza listy) · powód. Powody są już gotowym tekstem
 * (`bulkSkipReason`), okno niczego nie tłumaczy ani nie interpretuje kodów — to powód, nie to
 * okno, mówi w razie potrzeby, że sprawę trzeba rozstrzygnąć osobno w oknie decyzji. Pod listą
 * samo zdanie, że pominięte sprawy zostają zaznaczone na liście (poza anulowanymi, które znikają
 * z listy po jej odświeżeniu).
 */

import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/Button';
import { Modal } from '@/components/Modal';

/** Jedna pominięta sprawa w oknie wyniku. */
export interface BulkSkippedItem {
  stepInstanceId: string;
  instanceNumber: number;
  /** Czego dotyczy sprawa (etykieta z wiersza listy); `null` = rekord niedostępny. */
  targetLabel: string | null;
  /** Gotowy do pokazania powód pominięcia. */
  reason: string;
}

export interface BulkAcceptResultDialogProps {
  open: boolean;
  /** Ile spraw zaakceptowano (zapisane decyzje, także te czekające na pozostałych akceptantów). */
  accepted: number;
  skipped: readonly BulkSkippedItem[];
  onClose: () => void;
  testIdPrefix?: string;
}

export function BulkAcceptResultDialog({
  open,
  accepted,
  skipped,
  onClose,
  testIdPrefix = 'bulk-result',
}: BulkAcceptResultDialogProps): ReactElement {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t('tenant.processes.approvals.bulk.resultTitle', {
        accepted,
        skipped: skipped.length,
      })}
      footer={
        <div className="flex justify-end">
          <Button size="sm" data-testid={`${testIdPrefix}-close`} onClick={onClose}>
            {t('common.close')}
          </Button>
        </div>
      }
    >
      <ul className="space-y-3 text-sm" data-testid={`${testIdPrefix}-skipped-list`}>
        {skipped.map((item) => (
          <li
            key={item.stepInstanceId}
            className="flex flex-col gap-0.5"
            data-testid={`${testIdPrefix}-skipped-${item.stepInstanceId}`}
          >
            <span className="font-medium text-text-primary">
              <span className="font-mono text-xs">#{item.instanceNumber}</span>
              {' · '}
              {item.targetLabel ?? t('tenant.processes.approvals.fallback.missingTarget')}
            </span>
            <span className="text-text-tertiary">{item.reason}</span>
          </li>
        ))}
      </ul>
      <p className="mt-4 text-sm text-text-tertiary" data-testid={`${testIdPrefix}-stay-selected`}>
        {t('tenant.processes.approvals.bulk.resultStaysSelected')}
      </p>
    </Modal>
  );
}
